// Native ERP connector pages (/integrations/:slug). One entry per connector, one shared page (pages/Connector.tsx).
// Every fact here must match the connector's code in the app repo. For Epicor Kinetic that is the
// Kinetic-side function library and queries plus the connection's capabilities (Oct 2026).
// To add IFS or SAP: add an entry keyed by its slug, and only once its connector is built.
// No customer names, no em dashes, no internal tool names.

export type ConnectorRead = {
  /** What the reader calls it */
  what: string;
  /** The ERP's own table names, for IT */
  tables: string;
  fields: string[];
  note: string;
};

export type Connector = {
  slug: string;
  name: string;
  meta: { title: string; description: string };
  hero: { title: [string, string]; lede: string };
  glance: { k: string; v: string }[];
  reads: { title: [string, string]; lede: string; items: ConnectorRead[]; notRead: string };
  writes: { title: [string, string]; lede: string; items: { t: string; d: string }[]; never: string[]; neverNote: string };
  connects: { title: [string, string]; lede: string; items: { k: string; v: string }[] };
  needs: { title: [string, string]; items: string[] };
  plan: { big: string; line: string; weeks: { w: string; t: string; d: string }[]; after: string };
  team: { title: [string, string]; lede: string };
  close: { title: [string, string]; lede: string };
};

export const connectors: Record<string, Connector> = {
  'epicor-kinetic': {
    slug: 'epicor-kinetic',
    name: 'Epicor Kinetic',
    meta: {
      title: 'Epicor Kinetic integration: what it reads, what it writes, how long it takes',
      description:
        'A native Epicor Kinetic connector that runs inside Kinetic. What it reads (parts, packing slips, serials, lots, customers), what it writes back, how it connects, what IT provides, and roughly four weeks to live on one product line.',
    },
    hero: {
      title: ['Epicor Kinetic,', 'connected.'],
      lede: 'Runs inside Kinetic, so each listed part ships with a scannable code.',
    },
    glance: [
      { k: 'Runs in', v: 'Kinetic, as an Epicor Function library and saved queries' },
      { k: 'Direction', v: 'Kinetic calls BrandedMark over HTTPS' },
      { k: 'Sign-in', v: 'A scoped API key you can rotate' },
      { k: 'Schedule', v: 'Kinetic’s own task agent, at the cadence you set' },
      { k: 'Scope', v: 'Only the parts you list' },
      { k: 'Time to live', v: 'Roughly four weeks' },
    ],
    reads: {
      title: ['What we read.', 'Only what the codes need.'],
      lede: 'Each run reads what changed since the last one, for listed parts only.',
      items: [
        {
          what: 'Parts',
          tables: 'Part',
          fields: ['PartNum', 'PartDescription', 'InActive', 'TrackSerialNum', 'TrackLots', 'ProdCode', 'ClassID'],
          note: 'Parts become products. Product group and part class map to your categories.',
        },
        {
          what: 'Packing slips',
          tables: 'ShipHead, ShipVia',
          fields: ['PackNum', 'ShipDate', 'ShipViaCode', 'TrackingNumber', 'Weight', 'WeightUOM', 'Description', 'TrackingURL'],
          note: 'Shipped, closed or invoiced slips only, with carrier and tracking link. Open slips never leave Kinetic.',
        },
        {
          what: 'Slip lines',
          tables: 'ShipDtl',
          fields: ['PackLine', 'PartNum', 'OurInventoryShipQty', 'IUM', 'LotNum', 'RevisionNum', 'TotalNetWeight', 'OrderNum', 'OrderLine'],
          note: 'Listed parts only. A mixed slip sends just its listed lines.',
        },
        {
          what: 'Serial numbers',
          tables: 'SerialNo',
          fields: ['SerialNumber'],
          note: 'One per unit shipped, so each unit gets its own record.',
        },
        {
          what: 'Customer order',
          tables: 'OrderHed, OrderDtl',
          fields: ['PONum', 'POLine'],
          note: 'The PO your customer knows the delivery by.',
        },
        {
          what: 'Customers',
          tables: 'Customer',
          fields: ['CustNum', 'Name', 'EMailAddress'],
          note: 'Only customers whose slips carry a listed part. Include or exclude by number.',
        },
        {
          what: 'Returns, if you switch them on',
          tables: 'RMAHead, RMADtl, RMARcpt, RMADisp',
          fields: ['RMANum', 'OpenRMA', 'ReturnQty', 'ReceivedQty', 'DispType', 'DispQty', 'ReasonCode'],
          note: 'Receipts, dispositions and closure for returns raised in BrandedMark.',
        },
      ],
      notRead: 'Not read: prices, costs, stock levels, invoices, customer addresses or phone numbers.',
    },
    writes: {
      title: ['What we write back.', 'Just what you need.'],
      lede: '',
      items: [
        {
          t: 'Its own settings',
          d: 'A user code type of its own holds its settings, part list and a bookmark, so each run resumes where the last one stopped.',
        },
        {
          t: 'Returns, if you switch them on',
          d: 'Approve a return in BrandedMark and an RMA appears in Kinetic with our return number. Receipt and disposition run in Kinetic as today, and the owner sees progress.',
        },
      ],
      never: ['Parts', 'Prices', 'Sales orders', 'Packing slips', 'Customers', 'Stock', 'Invoices'],
      neverNote: 'Never written. Kinetic stays the master.',
    },
    connects: {
      title: ['How it connects.', 'In Kinetic’s own tools.'],
      lede: 'No server install and no middleware. Your team can open all of it in Kinetic.',
      items: [
        { k: 'Where it runs', v: 'An Epicor Function library and BAQs, in the company you sync.' },
        { k: 'Direction', v: 'Kinetic calls BrandedMark over HTTPS. Nothing calls in while it runs.' },
        { k: 'Sign-in', v: 'A BrandedMark API key scoped to what you switch on. Shown once, kept in Kinetic, rotatable at any time.' },
        { k: 'Frequency', v: 'A System Agent task at the cadence you choose, sending only what changed.' },
        { k: 'When something is wrong', v: 'Kinetic shows an error and the task holds its place until the record is fixed. Nothing is skipped.' },
        { k: 'Setup', v: 'We install it through Kinetic’s v2 REST API, with an account you create.' },
      ],
    },
    needs: {
      title: ['What IT provides.', ''],
      items: [
        'A test company to start in, if you have one.',
        'A connector user with v2 REST API access and a Kinetic API key, licensed for that company.',
        'Permission for it to add a function library, BAQs and user codes there.',
        'A System Agent scheduled task for the sync.',
        'Outbound HTTPS from your Kinetic app server to BrandedMark.',
        'The starting parts, and someone who knows them.',
        'If returns are on, your disposition types and reason codes.',
      ],
    },
    plan: {
      big: 'Roughly four weeks',
      line: 'From kick-off to live on one product line.',
      weeks: [
        { w: 'Week 1', t: 'Kick-off', d: 'Agree the line and its parts. IT sets up the connector user.' },
        { w: 'Week 2', t: 'Install', d: 'We install in your test company and map product groups to categories.' },
        { w: 'Week 3', t: 'Test', d: 'Real slips run through. Your team scans them and checks every field.' },
        { w: 'Week 4', t: 'Live', d: 'The schedule goes on in your live company. Every slip for that line carries its code.' },
      ],
      after: 'Add a product line by adding its parts to the list.',
    },
    team: {
      title: ['Built by people', 'who built ERP.'],
      lede: 'The connector speaks Kinetic: its tables, its functions, its scheduler. Your IT team can read every line.',
    },
    close: {
      title: ['Questions about', 'your Kinetic?'],
      lede: 'Bring your Kinetic setup and a product line. We will tell you how it fits.',
    },
  },
};

export const connectorSlugs = Object.keys(connectors);
